import React, { useState, useEffect, useRef } from 'react';
import { useAssessment } from '../../hooks/useAssessment';
import { useCompliance } from '../../hooks/useCompliance';
import { PieChart, Pie, Cell } from 'recharts';
import { questions } from '../../data/questions';
import { CONTROL_TYPES, ANSWER_TYPES } from '../../constants/assessmentTypes';
import html2pdf from 'html2pdf.js';
import { toast } from 'react-hot-toast';

const ExecutiveSummaryReport = ({ 
  assessmentName, 
  maturityLevel, 
  compliances, 
  answers,
  summaryRecommendations,
  setSummaryRecommendations
}) => {
  const reportRef = useRef(null);

  // Move controlMapping to the top
  const controlMapping = {
    'Patch Applications': CONTROL_TYPES.PATCH_APPLICATIONS,
    'Patch Operating Systems': CONTROL_TYPES.PATCH_OS,
    'Multi-factor Authentication': CONTROL_TYPES.MFA,
    'Restrict Administrative Privileges': CONTROL_TYPES.ADMIN_PRIVILEGES,
    'Application Control': CONTROL_TYPES.APP_CONTROL,
    'Microsoft Office Macro Settings': CONTROL_TYPES.OFFICE_MACROS,
    'User Application Hardening': CONTROL_TYPES.APP_HARDENING,
    'Regular Backups': CONTROL_TYPES.BACKUPS
  };

  const { 
    background, 
    setBackground, 
    scope, 
    setScope, 
    limitations, 
    setLimitations,
    saveAssessment,
    savedAssessments,
    currentAssessmentName
  } = useAssessment();

  // Add useEffect to update fields when assessment changes
  useEffect(() => {
    if (assessmentName) {
      const currentAssessment = savedAssessments.find(a => a.name === assessmentName);
      if (currentAssessment) {
        setBackground(currentAssessment.background || '');
        setScope(currentAssessment.scope || '');
        setLimitations(currentAssessment.limitations || '');
      }
    }
  }, [assessmentName, savedAssessments]);

  const [controlMLs, setControlMLs] = useState({
    'Patch Applications': 'ML0',
    'Patch Operating Systems': 'ML0',
    'Multi-factor Authentication': 'ML0',
    'Restrict Administrative Privileges': 'ML0',
    'Application Control': 'ML0',
    'Microsoft Office Macro Settings': 'ML0',
    'User Application Hardening': 'ML0',
    'Regular Backups': 'ML0'
  });

  useEffect(() => {
    if (answers) {
      const calculateControlML = (controlAnswers) => {
        return 'ML0';
      };

      const newControlMLs = {};
      Object.keys(controlMLs).forEach(control => {
        newControlMLs[control] = calculateControlML(answers[control]);
      });
      setControlMLs(newControlMLs);
    }
  }, [answers]);

  // Calculate progress for a control at a specific maturity level
  const calculateProgress = (controlName, level) => {
    // Remove numbering from control name
    const cleanName = controlName.replace(/^\d+\.\s*/, '');
    const controlType = controlMapping[cleanName];
    
    // Special handling for Patch Operating Systems
    const isPatchOS = cleanName === 'Patch Operating Systems';
    
    // Get questions for all levels up to and including the current level
    const levels = ['ML1', 'ML2', 'ML3'];
    const levelIndex = levels.indexOf(level);
    const requiredLevels = levels.slice(0, levelIndex + 1);
    
    // Check if all previous levels are complete
    let allPreviousComplete = true;
    let currentProgress = 0;

    for (const checkLevel of requiredLevels) {
      const controlQuestions = questions[controlType]?.[checkLevel] || [];
      let levelCompletedCount = 0;
      let totalApplicableQuestions = 0;

      // For Patch OS, if checking ML2 and ML1 is complete, consider ML2 complete
      if (isPatchOS && checkLevel === 'ML2') {
        const ml1Questions = questions[controlType]?.['ML1'] || [];
        const ml1CompletedCount = ml1Questions.filter(q => {
          const answer = answers?.[q.id]?.value;
          return answer === 'effective' || answer === 'not_applicable';
        }).length;
        const ml1Complete = ml1Questions.length > 0 && ml1CompletedCount === ml1Questions.length;
        
        if (ml1Complete) {
          currentProgress = 1;
          continue;
        }
      }

      controlQuestions.forEach(q => {
        const answer = answers?.[q.id];
        
        if (answer?.value === 'not_applicable') {
          // Don't count not_applicable in either numerator or denominator
          return;
        }
        
        if (answer?.value === 'effective') {
          levelCompletedCount++;
        }
        
        // Count all questions except not_applicable ones in total
        totalApplicableQuestions++;
      });

      // Calculate progress based on applicable questions only
      const levelProgress = totalApplicableQuestions > 0 ? 
        levelCompletedCount / totalApplicableQuestions : 
        (isPatchOS && checkLevel === 'ML2' ? 1 : 0);

      // If this is the current level we're calculating for, store its progress
      if (checkLevel === level) {
        currentProgress = levelProgress;
      }
      // If this is a previous level and it's not complete, mark allPreviousComplete as false
      else if (levelProgress < 1) {
        allPreviousComplete = false;
      }

      console.log(`${cleanName} ${checkLevel} progress:`, {
        levelCompletedCount,
        totalApplicableQuestions,
        levelProgress,
        allPreviousComplete,
        isPatchOS
      });
    }

    // For Patch OS ML2, if ML1 is complete, return 1 (complete)
    if (isPatchOS && level === 'ML2') {
      const ml1Questions = questions[controlType]?.['ML1'] || [];
      const ml1CompletedCount = ml1Questions.filter(q => {
        const answer = answers?.[q.id]?.value;
        return answer === 'effective' || answer === 'not_applicable';
      }).length;
      const ml1Complete = ml1Questions.length > 0 && ml1CompletedCount === ml1Questions.length;
      
      if (ml1Complete) {
        return 1;
      }
    }

    // Return 0 if any previous level is incomplete, otherwise return current level progress
    return allPreviousComplete ? currentProgress : 0;
  };

  // Calculate raw progress for a control at a specific maturity level (for display purposes)
  const calculateRawProgress = (controlName, level) => {
    // Remove numbering from control name
    const cleanName = controlName.replace(/^\d+\.\s*/, '');
    const controlType = controlMapping[cleanName];
    
    // Special handling for Patch Operating Systems
    const isPatchOS = cleanName === 'Patch Operating Systems';
    
    // For Patch OS ML2, if ML1 is complete, return 1 (complete)
    if (isPatchOS && level === 'ML2') {
      const ml1Questions = questions[controlType]?.['ML1'] || [];
      const ml1CompletedCount = ml1Questions.filter(q => {
        const answer = answers?.[q.id]?.value;
        return answer === 'effective' || answer === 'not_applicable';
      }).length;
      const ml1Complete = ml1Questions.length > 0 && ml1CompletedCount === ml1Questions.length;
      
      if (ml1Complete) {
        return 1;
      }
    }

    // Get questions for the specific level only
    const controlQuestions = questions[controlType]?.[level] || [];
    let levelCompletedCount = 0;
    let totalApplicableQuestions = 0;

    controlQuestions.forEach(q => {
      const answer = answers?.[q.id];
      
      if (answer?.value === 'not_applicable') {
        // Don't count not_applicable in either numerator or denominator
        return;
      }
      
      if (answer?.value === 'effective') {
        levelCompletedCount++;
      }
      
      // Count all questions except not_applicable ones in total
      totalApplicableQuestions++;
    });

    // Calculate progress based on applicable questions only
    return totalApplicableQuestions > 0 ? 
      levelCompletedCount / totalApplicableQuestions : 
      (isPatchOS && level === 'ML2' ? 1 : 0);
  };

  // Add this function to calculate overall maturity level
  const calculateOverallMaturityLevel = (controls) => {
    // Get the achieved level for each control
    const controlLevels = controls.map(control => {
      const ml1Progress = calculateProgress(control, 'ML1');
      const ml2Progress = calculateProgress(control, 'ML2');
      const ml3Progress = calculateProgress(control, 'ML3');

      if (ml3Progress === 1 && ml2Progress === 1 && ml1Progress === 1) return 'ML3';
      if (ml2Progress === 1 && ml1Progress === 1) return 'ML2';
      if (ml1Progress === 1) return 'ML1';
      return 'ML0';
    });

    // Find the lowest level - this will be our overall level
    const lowestLevel = controlLevels.reduce((lowest, current) => {
      const levels = ['ML0', 'ML1', 'ML2', 'ML3'];
      return levels.indexOf(current) < levels.indexOf(lowest) ? current : lowest;
    }, 'ML3');

    // Check if all controls are at the same level
    const allSameLevel = controlLevels.every(level => level === lowestLevel);

    return {
      level: lowestLevel,
      allSameLevel,
      controlLevels
    };
  };

  // Inside the ExecutiveSummaryReport component, update the Overall Maturity Level section
  const controls = [
    '1. Patch Applications',
    '2. Patch Operating Systems',
    '3. Multi-factor Authentication',
    '4. Restrict Administrative Privileges',
    '5. Application Control',
    '6. Microsoft Office Macro Settings',
    '7. User Application Hardening',
    '8. Regular Backups'
  ];

  const overallML = calculateOverallMaturityLevel(controls);

  const E8ControlCard = ({ 
    title, 
    description, 
    controlName, 
    maturityLevel, 
    answers = {},
    calculateProgress,
    className = '' 
  }) => {
    // Get control type from mapping
    const cleanName = controlName?.replace(/^\d+\.\s*/, '') || '';
    const controlType = controlMapping[cleanName] || '';

    // Get description from questions data
    const getControlDescription = () => {
      const controlQuestions = questions[controlType];
      if (!controlQuestions || !controlQuestions.ML1) return '';

      // Get first question from each maturity level as the description
      const descriptions = [];
      
      if (controlQuestions.ML1 && controlQuestions.ML1.length > 0) {
        descriptions.push(`ML1: ${controlQuestions.ML1[0].question}`);
      }
      
      if (maturityLevel === 'ML2' || maturityLevel === 'ML3') {
        if (controlQuestions.ML2 && controlQuestions.ML2.length > 0) {
          descriptions.push(`ML2: ${controlQuestions.ML2[0].question}`);
        }
      }
      
      if (maturityLevel === 'ML3') {
        if (controlQuestions.ML3 && controlQuestions.ML3.length > 0) {
          descriptions.push(`ML3: ${controlQuestions.ML3[0].question}`);
        }
      }

      return descriptions.join('\n');
    };

    // Get audit data for this control
    const auditData = React.useMemo(() => {
      const controlAnswers = answers[controlType] || {};
      return {
        findings: controlAnswers.findings || '',
        justification: controlAnswers.justification || '',
        recommendations: controlAnswers.recommendations || ''
      };
    }, [answers, controlType]);

    // Add getStatusDisplay function
    const getStatusDisplay = (status) => {
      switch(status) {
        case 'effective':
          return { text: 'Effective', class: 'bg-green-100 text-green-800' };
        case 'ineffective':
          return { text: 'Ineffective', class: 'bg-yellow-100 text-yellow-800' };
        case 'not_implemented':
          return { text: 'Not Implemented', class: 'bg-red-100 text-red-800' };
        case 'alternate_control':
          return { text: 'Alternate Control', class: 'bg-blue-100 text-blue-800' };
        case 'no_visibility':
          return { text: 'No Visibility', class: 'bg-purple-100 text-purple-800' };
        case 'not_applicable':
          return { text: 'Not Applicable', class: 'bg-gray-100 text-gray-600' };
        case 'not_assessed':
          return { text: 'Not Assessed', class: 'bg-gray-100 text-gray-800' };
        default:
          console.log('Unhandled status:', status);
          return { text: status.replace(/_/g, ' '), class: 'bg-gray-100 text-gray-800' };
      }
    };

    // Calculate how many pie charts to show based on maturity level
    const levels = [];
    if (maturityLevel === 'ML1') levels.push('ML1');
    if (maturityLevel === 'ML2') levels.push('ML1', 'ML2');
    if (maturityLevel === 'ML3') levels.push('ML1', 'ML2', 'ML3');

    // Ensure questions exist for this control
    const controlQuestions = questions[controlType] || {};

    // Calculate detailed implementation stats for each level
    const getImplementationDetails = (level) => {
      if (!controlType || !questions[controlType] || !questions[controlType][level]) {
        return {
          total: 0,
          effective: 0,
          ineffective: 0,
          notImplemented: 0,
          alternateControl: 0,
          noVisibility: 0,
          notApplicable: 0,
          notAssessed: 0,
          progress: 0
        };
      }

      const levelQuestions = questions[controlType][level] || [];
      
      // Count each status type
      const counts = levelQuestions.reduce((acc, question) => {
        const answer = answers?.[question.id];
        const status = answer?.value || 'not_assessed';
        
        switch(status) {
          case 'effective':
            acc.effective++;
            break;
          case 'ineffective':
            acc.ineffective++;
            break;
          case 'not_implemented':
            acc.notImplemented++;
            break;
          case 'alternate_control':
            acc.alternateControl++;
            break;
          case 'no_visibility':
            acc.noVisibility++;
            break;
          case 'not_applicable':
            acc.notApplicable++;
            break;
          case 'not_assessed':
            acc.notAssessed++;
            break;
          default:
            console.log('Unhandled status in counts:', status);
            acc.notAssessed++;
            break;
        }
        return acc;
      }, {
        effective: 0,
        ineffective: 0,
        notImplemented: 0,
        alternateControl: 0,
        noVisibility: 0,
        notApplicable: 0,
        notAssessed: 0
      });

      // Calculate progress based on effective controls and alternate controls
      const progress = levelQuestions.length > 0 ? 
        ((counts.effective + counts.alternateControl) / levelQuestions.length) * 100 : 0;

      return {
        total: levelQuestions.length,
        ...counts,
        progress
      };
    };

    // Get implementation details for each level
    const implementationDetails = levels.reduce((acc, level) => {
      acc[level] = getImplementationDetails(level);
      return acc;
    }, {});

    // Calculate compliance for each level
    const complianceData = levels.map(level => {
      const progress = calculateProgress ? calculateProgress(controlName, level) : 0;
      return {
        level,
        data: [
          { name: 'Implemented', value: progress * 100 },
          { name: 'Not Implemented', value: 100 - (progress * 100) }
        ]
      };
    });

    // Colors for the pie charts
    const COLORS = ['#22c55e', '#e5e7eb'];

    // Get overall status
    const getOverallStatus = () => {
      const highestLevel = levels[levels.length - 1];
      const progress = calculateProgress ? calculateProgress(controlName, highestLevel) : 0;
      
      if (progress === 1) return { text: 'Fully Implemented', class: 'text-green-800 bg-green-100' };
      if (progress > 0) return { text: 'Partial Implementation', class: 'text-yellow-800 bg-yellow-100' };
      return { text: 'Not Implemented', class: 'text-red-800 bg-red-100' };
    };

    const status = getOverallStatus();

    // Implementation notes for each control and level
    const implementationNotes = {
      'Patch Applications': {
        ML1: 'Patches/mitigates extreme risk vulnerabilities within 48 hours',
        ML2: 'Patches/mitigates high or critical risk vulnerabilities within two weeks',
        ML3: 'Patches/mitigates medium risk vulnerabilities within one month'
      },
      'Patch Operating Systems': {
        ML1: 'Patches critical vulnerabilities in 48 hours, others in 2-4 weeks',
        ML2: 'Maintains patching standards established in ML1',
        ML3: 'Extends patching to drivers and firmware, uses latest OS versions'
      }
      // Add notes for other controls as needed
    };

    return (
      <div className={`border rounded-lg p-6 transition-all duration-200 hover:shadow-md ${className}`}>
        {/* Header Section */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">{title}</h3>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${status.class}`}>
            {status.text}
          </span>
        </div>

        {/* Main Content */}
        <div className="flex flex-col space-y-6">
          {/* Implementation Status Section */}
          <div className="space-y-6">
            {Object.entries(implementationDetails).map(([level, details]) => {
              const levelQuestions = questions[controlType]?.[level] || [];
              const levelProgress = calculateProgress ? calculateProgress(controlName, level) : 0;
              
              return (
                <div key={level} className="bg-white bg-opacity-80 rounded-lg p-4">
                  {/* Level Header */}
                  <div className="flex flex-col space-y-3 mb-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <h4 className="text-xl font-bold text-gray-900">{level}</h4>
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-semibold rounded-full">
                          {levelQuestions.length} Controls
                        </span>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="relative">
                          <PieChart width={100} height={100}>
                            <Pie
                              data={[
                                { name: 'Implemented', value: Math.round(levelProgress * 100) },
                                { name: 'Not Implemented', value: 100 - Math.round(levelProgress * 100) }
                              ]}
                              innerRadius={30}
                              outerRadius={45}
                              paddingAngle={0}
                              dataKey="value"
                            >
                              {[0, 1].map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index]} />
                              ))}
                            </Pie>
                          </PieChart>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-sm font-medium">{Math.round(levelProgress * 100)}%</span>
                          </div>
                        </div>
                        <span className="text-sm font-medium text-gray-600">
                          {Math.round(levelProgress * 100)}% Complete
                        </span>
                      </div>
                    </div>
                    <div className="bg-white border-l-4 border-blue-500 p-4 rounded-r-lg">
                      <p className="text-base text-gray-700 font-medium">
                        {/* Patch Applications */}
                        {level === 'ML1' && controlName === 'Patch Applications' && 
                          "Patches/mitigates extreme risk vulnerabilities within 48 hours."}
                        {level === 'ML2' && controlName === 'Patch Applications' && 
                          "Patches/mitigates high or critical risk vulnerabilities within two weeks."}
                        {level === 'ML3' && controlName === 'Patch Applications' && 
                          "Patches/mitigates medium risk vulnerabilities within one month."}

                        {/* Patch Operating Systems */}
                        {level === 'ML1' && controlName === 'Patch Operating Systems' && 
                          "Patches critical vulnerabilities in 48 hours, others in 2-4 weeks."}
                        {level === 'ML2' && controlName === 'Patch Operating Systems' && 
                          "Maintains patching standards established in ML1."}
                        {level === 'ML3' && controlName === 'Patch Operating Systems' && 
                          "Extends patching to drivers and firmware, uses latest OS versions."}

                        {/* Multi-factor Authentication */}
                        {level === 'ML1' && controlName === 'Multi-factor Authentication' && 
                          "Implements MFA for remote access and important data/actions."}
                        {level === 'ML2' && controlName === 'Multi-factor Authentication' && 
                          "Extends MFA to all users, uses phishing-resistant methods."}
                        {level === 'ML3' && controlName === 'Multi-factor Authentication' && 
                          "Implements phishing-resistant MFA for all users and data repositories."}

                        {/* Restrict Administrative Privileges */}
                        {level === 'ML1' && controlName === 'Restrict Administrative Privileges' && 
                          "Validates privilege requests, uses separate admin accounts."}
                        {level === 'ML2' && controlName === 'Restrict Administrative Privileges' && 
                          "Implements time-based access, auditing, and jump servers."}
                        {level === 'ML3' && controlName === 'Restrict Administrative Privileges' && 
                          "Uses Just-In-Time administration and secure admin workstations."}

                        {/* Application Control */}
                        {level === 'ML1' && controlName === 'Application Control' && 
                          "Restricts execution on workstations to an approved set."}
                        {level === 'ML2' && controlName === 'Application Control' && 
                          "Extends control to internet-facing servers and all disk locations."}
                        {level === 'ML3' && controlName === 'Application Control' && 
                          "Includes driver execution restriction and vulnerable driver blocklist."}

                        {/* Microsoft Office Macro Settings */}
                        {level === 'ML1' && controlName === 'Microsoft Office Macro Settings' && 
                          "Microsoft Office macros are disabled for users that do not have a demonstrated business requirement."}
                        {level === 'ML2' && controlName === 'Microsoft Office Macro Settings' && 
                          "Microsoft Office macros in files originating from the internet are blocked."}
                        {level === 'ML3' && controlName === 'Microsoft Office Macro Settings' && 
                          "Microsoft Office macro antivirus scanning is enabled."}

                        {/* User Application Hardening */}
                        {level === 'ML1' && controlName === 'User Application Hardening' && 
                          "Internet Explorer 11 is disabled or removed."}
                        {level === 'ML2' && controlName === 'User Application Hardening' && 
                          "Web browsers do not process Java from the internet."}
                        {level === 'ML3' && controlName === 'User Application Hardening' && 
                          "Web browsers do not process Java from the internet."}

                        {/* Regular Backups */}
                        {level === 'ML1' && controlName === 'Regular Backups' && 
                          "Performs regular backups, restricts access, tests annually."}
                        {level === 'ML2' && controlName === 'Regular Backups' && 
                          "Prevents modification/deletion by unprivileged accounts."}
                        {level === 'ML3' && controlName === 'Regular Backups' && 
                          "Further restricts backup access and modification rights."}
                      </p>
                    </div>
                  </div>

                  {/* Controls Table */}
                  <div className="mb-4">
                    <div className="grid grid-cols-12 gap-4 mb-2 bg-gradient-to-r from-blue-50 to-white p-3 rounded-t-lg border-b-2 border-blue-200">
                      <div className="col-span-2 text-sm font-medium text-blue-800">Control ID</div>
                      <div className="col-span-7 text-sm font-medium text-blue-800">Description</div>
                      <div className="col-span-3 text-sm font-medium text-blue-800 text-right">Status</div>
                    </div>

                    <div className="space-y-4">
                      {levelQuestions.map((question) => {
                        const answer = answers?.[question.id] || {};
                        const status = answer.value || 'not_assessed';
                        const statusDisplay = getStatusDisplay(status);
                        const justification = answer.justification || '';
                        
                        return (
                          <div key={question.id} className="border border-gray-100 rounded-lg overflow-hidden">
                            {/* Question Row - Enhanced with stronger contrast between rows */}
                            <div className={`grid grid-cols-12 gap-4 p-4 ${
                              parseInt(question.id.match(/\d+$/)[0]) % 2 === 1 ? 'bg-blue-50' : 'bg-white'
                            }`}>
                              <div className="col-span-2">
                                <span className="text-sm font-mono text-gray-600">{question.id}</span>
                              </div>
                              <div className="col-span-7">
                                <span className="text-sm font-medium text-gray-800">{question.question}</span>
                              </div>
                              <div className="col-span-3 flex justify-end">
                                <span className={`px-3 py-1 rounded-md text-sm font-medium ${statusDisplay.class}`}>
                                  {statusDisplay.text}
                                </span>
                              </div>
                            </div>

                            {/* Enhanced Justification & Recommendations Section */}
                            {(justification || answer.recommendations) && (
                              <div className={`border-l-4 border-gray-300 ml-4 ${
                                parseInt(question.id.match(/\d+$/)[0]) % 2 === 1 ? 'bg-blue-50' : 'bg-white'
                              }`}>
                                <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-200">
                                  {/* Justification Column */}
                                  {justification && (
                                    <div className="p-5">
                                      <div className="flex flex-col">
                                        <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-600 mb-3 flex items-center">
                                          <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                          </svg>
                                          Assessment Justification
                                        </h4>
                                        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                                          <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                                            {justification}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* Recommendations Column */}
                                  <div className="p-5">
                                    <div className="flex flex-col">
                                      <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-600 mb-3 flex items-center">
                                        <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                        </svg>
                                        Recommendations
                                      </h4>
                                      <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                                        <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                                          {answer.recommendations || 'No recommendations provided.'}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Summary Stats */}
                  <div className="border-t border-gray-200 pt-3">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
                      {details.effective > 0 && (
                        <span className="flex items-center">
                          <span className="w-2 h-2 rounded-full bg-green-500 mr-2"></span>
                          {details.effective} Effective
                        </span>
                      )}
                      {details.ineffective > 0 && (
                        <span className="flex items-center">
                          <span className="w-2 h-2 rounded-full bg-yellow-500 mr-2"></span>
                          {details.ineffective} Ineffective
                        </span>
                      )}
                      {details.notImplemented > 0 && (
                        <span className="flex items-center">
                          <span className="w-2 h-2 rounded-full bg-red-500 mr-2"></span>
                          {details.notImplemented} Not Implemented
                        </span>
                      )}
                      {details.alternateControl > 0 && (
                        <span className="flex items-center">
                          <span className="w-2 h-2 rounded-full bg-blue-500 mr-2"></span>
                          {details.alternateControl} Alternate Control
                        </span>
                      )}
                      {details.noVisibility > 0 && (
                        <span className="flex items-center">
                          <span className="w-2 h-2 rounded-full bg-purple-500 mr-2"></span>
                          {details.noVisibility} No Visibility
                        </span>
                      )}
                      {details.notApplicable > 0 && (
                        <span className="flex items-center">
                          <span className="w-2 h-2 rounded-full bg-gray-500 mr-2"></span>
                          {details.notApplicable} Not Applicable
                        </span>
                      )}
                      {details.notAssessed > 0 && (
                        <span className="flex items-center">
                          <span className="w-2 h-2 rounded-full bg-gray-300 mr-2"></span>
                          {details.notAssessed} Not Assessed
                        </span>
                      )}
                    </div>
                    <div className="mt-2 text-sm text-gray-500">
                      {implementationNotes?.[cleanName]?.[level]}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const handleSaveReport = () => {
    // Save the assessment using the assessmentName prop
    saveAssessment(assessmentName);
  };

  const handleExportPDF = async () => {
    const element = reportRef.current;
    const opt = {
      margin: [15, 15, 15, 15],
      filename: `${assessmentName || 'Report'}_${new Date().toISOString().split('T')[0]}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { 
        scale: 2,
        useCORS: true,
        logging: true,
        letterRendering: true,
        scrollY: -window.scrollY,
        windowWidth: element.scrollWidth
      },
      jsPDF: { 
        unit: 'mm', 
        format: 'a4', 
        orientation: 'portrait',
        compress: true,
        precision: 16
      },
      pagebreak: { 
        mode: ['avoid-all', 'css', 'legacy'],
        before: '.page-break-before',
        after: '.page-break-after',
        avoid: ['.avoid-break', '.E8ControlCard', '.maturity-section', 'table', 'img']
      }
    };

    // Add page break classes before export
    const sections = element.querySelectorAll('.bg-white.rounded-lg.shadow.p-6');
    sections.forEach(section => {
      section.classList.add('avoid-break');
      section.style.pageBreakInside = 'avoid';
      section.style.breakInside = 'avoid';
    });

    const controlCards = element.querySelectorAll('.E8ControlCard');
    controlCards.forEach(card => {
      card.classList.add('avoid-break');
      card.style.pageBreakInside = 'avoid';
      card.style.breakInside = 'avoid';
    });

    // Show loading state
    const loadingToast = toast.loading('Generating PDF...');

    try {
      await html2pdf().set(opt).from(element).save();
      toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('PDF generation failed:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      toast.dismiss(loadingToast);
      
      // Clean up added classes
      sections.forEach(section => {
        section.classList.remove('avoid-break');
        section.style.pageBreakInside = '';
        section.style.breakInside = '';
      });
      
      controlCards.forEach(card => {
        card.classList.remove('avoid-break');
        card.style.pageBreakInside = '';
        card.style.breakInside = '';
      });
    }
  };

  return (
    <div ref={reportRef} className="space-y-6 pdf-report">
      {/* Assessment Overview - Top of page */}
      <div className="bg-white rounded-lg shadow p-6 avoid-break">
        <h2 className="text-lg font-medium text-gray-900 mb-6">Assessment Overview</h2>
        <div className="grid grid-cols-2 gap-y-6">
          <div>
            <p className="text-sm text-gray-500">Assessment Name</p>
            <p className="text-xl font-medium text-gray-900">{assessmentName || 'Unnamed Assessment'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Target Maturity Level</p>
            <p className="text-xl font-medium text-gray-900">{maturityLevel || 'ML1'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Assessment Date</p>
            <p className="text-xl font-medium text-gray-900">11/01/2025</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Overall Compliance</p>
            <p className="text-xl font-medium text-gray-900">
              {compliances ? `${Math.round(compliances[maturityLevel] || 0)}%` : '0%'}
            </p>
          </div>
        </div>
      </div>

      {/* Background & Scope section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Background & Scope</h2>
        <div className="space-y-4">
          {/* Entity & System Background */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Entity & System Background
            </label>
            <textarea
              value={background}
              onChange={(e) => setBackground(e.target.value)}
              className="w-full h-32 p-3 border rounded-md text-sm text-gray-600"
              placeholder="Provide background information about the entity and system being assessed..."
            />
          </div>

          {/* Assessment Scope & Approach */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assessment Scope & Approach
            </label>
            <textarea
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              className="w-full h-32 p-3 border rounded-md text-sm text-gray-600"
              placeholder="Describe the scope and approach of this assessment..."
            />
          </div>

          {/* Limitations */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assessment Limitations
            </label>
            <textarea
              value={limitations}
              onChange={(e) => setLimitations(e.target.value)}
              className="w-full h-24 p-3 border rounded-md text-sm text-gray-600"
              placeholder="Document any limitations encountered during the assessment..."
            />
          </div>
        </div>
      </div>

      {/* Maturity Level Summary */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Maturity Level Summary</h2>
        
        {/* Overall Maturity Level Status */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Overall Maturity Level</h3>
          
          <div className="grid grid-cols-3 gap-4">
            {/* Current Level Card */}
            <div className="bg-white border rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-medium text-gray-500">Current Level</h4>
                <span className={`
                  px-2.5 py-0.5 rounded-full text-xs font-medium
                  ${overallML.level === 'ML0' ? 'bg-gray-100 text-gray-600' :
                    overallML.level === 'ML1' ? 'bg-blue-100 text-blue-600' :
                    overallML.level === 'ML2' ? 'bg-purple-100 text-purple-600' :
                    'bg-indigo-100 text-indigo-600'}
                `}>
                  {overallML.level}
                </span>
              </div>
              <div className="flex items-center justify-center py-4">
                <div className={`
                  h-24 w-24 rounded-full flex items-center justify-center text-2xl font-bold
                  ${overallML.level === 'ML0' ? 'bg-gray-100 text-gray-600' :
                    overallML.level === 'ML1' ? 'bg-blue-100 text-blue-600' :
                    overallML.level === 'ML2' ? 'bg-purple-100 text-purple-600' :
                    'bg-indigo-100 text-indigo-600'}
                `}>
                  {overallML.level}
                </div>
              </div>
              <p className="text-sm text-center text-gray-600 mt-4">
                {overallML.level === 'ML0' ? 'Initial Assessment Required' :
                 `${overallML.level} Controls Implemented`}
              </p>
            </div>

            {/* Target & Progress Card */}
            <div className="bg-white border rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-medium text-gray-500">Target & Progress</h4>
                <span className={`
                  px-2.5 py-0.5 rounded-full text-xs font-medium
                  bg-blue-100 text-blue-600
                `}>
                  Target: {maturityLevel}
                </span>
              </div>
              <div className="space-y-4">
                {/* Progress Calculation */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600">Overall Progress</span>
                    <span className="text-2xl font-semibold text-gray-900">
                      {(() => {
                        // Calculate progress for each control
                        const controlProgress = controls.map(control => {
                          const ml1Progress = calculateProgress(control, 'ML1');
                          const ml2Progress = calculateProgress(control, 'ML2');
                          const ml3Progress = calculateProgress(control, 'ML3');
                          
                          // Calculate weighted progress based on target level
                          let progress = 0;
                          if (maturityLevel === 'ML1') {
                            progress = ml1Progress;
                          } else if (maturityLevel === 'ML2') {
                            progress = (ml1Progress + (ml1Progress === 1 ? ml2Progress : 0)) / 2;
                          } else if (maturityLevel === 'ML3') {
                            progress = (ml1Progress + 
                              (ml1Progress === 1 ? ml2Progress : 0) + 
                              (ml1Progress === 1 && ml2Progress === 1 ? ml3Progress : 0)) / 3;
                          }
                          return progress;
                        });

                        // Calculate overall progress percentage
                        const totalProgress = controlProgress.reduce((sum, p) => sum + p, 0);
                        const progressPercentage = Math.round((totalProgress / controls.length) * 100);
                        return `${progressPercentage}%`;
                      })()}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 rounded-full h-2" 
                      style={{ 
                        width: (() => {
                          const controlProgress = controls.map(control => {
                            const ml1Progress = calculateProgress(control, 'ML1');
                            const ml2Progress = calculateProgress(control, 'ML2');
                            const ml3Progress = calculateProgress(control, 'ML3');
                            
                            let progress = 0;
                            if (maturityLevel === 'ML1') {
                              progress = ml1Progress;
                            } else if (maturityLevel === 'ML2') {
                              progress = (ml1Progress + (ml1Progress === 1 ? ml2Progress : 0)) / 2;
                            } else if (maturityLevel === 'ML3') {
                              progress = (ml1Progress + 
                                (ml1Progress === 1 ? ml2Progress : 0) + 
                                (ml1Progress === 1 && ml2Progress === 1 ? ml3Progress : 0)) / 3;
                            }
                            return progress;
                          });

                          const totalProgress = controlProgress.reduce((sum, p) => sum + p, 0);
                          return `${Math.round((totalProgress / controls.length) * 100)}%`;
                        })()
                      }}
                    />
                  </div>
                </div>

                {/* Status Indicator */}
                <div>
                  {(() => {
                    // Calculate how many controls are at each level
                    const controlLevels = controls.map(control => {
                      const ml1Progress = calculateProgress(control, 'ML1');
                      const ml2Progress = calculateProgress(control, 'ML2');
                      const ml3Progress = calculateProgress(control, 'ML3');

                      if (ml3Progress === 1 && ml2Progress === 1 && ml1Progress === 1) return 'ML3';
                      if (ml2Progress === 1 && ml1Progress === 1) return 'ML2';
                      if (ml1Progress === 1) return 'ML1';
                      if (ml1Progress > 0) return 'IN_PROGRESS';
                      return 'ML0';
                    });

                    const atTarget = controlLevels.filter(level => level === maturityLevel).length;
                    const aboveTarget = controlLevels.filter(level => {
                      const levels = ['ML0', 'ML1', 'ML2', 'ML3'];
                      return levels.indexOf(level) > levels.indexOf(maturityLevel);
                    }).length;
                    const belowTarget = controlLevels.filter(level => {
                      const levels = ['ML0', 'ML1', 'ML2', 'ML3'];
                      return levels.indexOf(level) < levels.indexOf(maturityLevel) && level !== 'ML0';
                    }).length;
                    const inProgress = controlLevels.filter(level => level === 'IN_PROGRESS').length;

                    let statusClass = '';
                    let statusText = '';

                    if (atTarget + aboveTarget === controls.length) {
                      statusClass = 'bg-green-100 text-green-800';
                      statusText = 'Target Level Achieved';
                    } else if (atTarget + aboveTarget + belowTarget + inProgress === 0) {
                      statusClass = 'bg-red-100 text-red-800';
                      statusText = 'Not Started';
                    } else if (atTarget + aboveTarget > 0) {
                      statusClass = 'bg-blue-100 text-blue-800';
                      statusText = `${atTarget + aboveTarget} Controls at or above Target`;
                    } else {
                      statusClass = 'bg-yellow-100 text-yellow-800';
                      statusText = 'Controls in Progress';
                    }

                    return (
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusClass}`}>
                        {statusText}
                      </span>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Status Summary Card */}
            <div className="bg-white border rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-medium text-gray-500">Implementation Status</h4>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Controls at Target</span>
                  <span className="font-medium text-gray-900">
                    {controls.filter(c => calculateProgress(c, maturityLevel) === 1).length} of {controls.length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Controls in Progress</span>
                  <span className="font-medium text-gray-900">
                    {controls.filter(c => {
                      const progress = calculateProgress(c, maturityLevel);
                      return progress > 0 && progress < 1;
                    }).length} of {controls.length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Controls Not Started</span>
                  <span className="font-medium text-gray-900">
                    {controls.filter(c => calculateProgress(c, maturityLevel) === 0).length} of {controls.length}
                  </span>
                </div>
                <div className="pt-3 mt-3 border-t">
                  <p className="text-sm text-gray-600">
                    {overallML.allSameLevel 
                      ? `All controls have achieved ${overallML.level}`
                      : 'Controls are at different maturity levels'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ML Level Labels */}
        <div className="flex mb-2 pl-64">
          <div className="flex-1 grid grid-cols-4 gap-1 px-4">
            <div className="text-xs text-center text-gray-500">ML0</div>
            <div className="text-xs text-center text-gray-500">ML1</div>
            <div className="text-xs text-center text-gray-500">ML2</div>
            <div className="text-xs text-center text-gray-500">ML3</div>
          </div>
          <div className="w-12"></div>
        </div>

        {/* Control Maturity Levels */}
        <div className="space-y-4">
          {[
            '1. Patch Applications',
            '2. Patch Operating Systems',
            '3. Multi-factor Authentication',
            '4. Restrict Administrative Privileges',
            '5. Application Control',
            '6. Microsoft Office Macro Settings',
            '7. User Application Hardening',
            '8. Regular Backups'
          ].map((control) => {
            const ml1Progress = calculateProgress(control, 'ML1');
            const ml2Progress = calculateProgress(control, 'ML2');
            const ml3Progress = calculateProgress(control, 'ML3');

            // Get raw progress for display purposes (shows actual progress regardless of prerequisites)
            const ml1RawProgress = calculateRawProgress(control, 'ML1');
            const ml2RawProgress = calculateRawProgress(control, 'ML2');
            const ml3RawProgress = calculateRawProgress(control, 'ML3');

            // Determine the current achieved level (highest completed incremental level)
            let achievedLevel = 'ML0';
            if (ml1Progress === 1) {
              achievedLevel = 'ML1';
              if (ml2Progress === 1) {
                achievedLevel = 'ML2';
                if (ml3Progress === 1) {
                  achievedLevel = 'ML3';
                }
              }
            }

            return (
              <div key={control} className="flex items-center justify-between group">
                <div className="w-64 text-sm text-gray-900">{control}</div>
                <div className="flex-1 mx-4">
                  <div className="grid grid-cols-4 gap-1">
                    {/* ML0 Base Level */}
                    <div className="relative">
                      <div className={`h-2 rounded-full bg-gray-200`}>
                        <div className={`h-2 rounded-full bg-emerald-500 ${achievedLevel !== 'ML0' ? 'w-full' : 'w-0'}`} />
                      </div>
                      {achievedLevel === 'ML0' && (
                        <div className="absolute -top-1 left-0 right-0 h-4 ring-2 ring-offset-2 ring-blue-500 rounded-full" />
                      )}
                    </div>

                    {/* ML1 Progress */}
                    <div className="relative">
                      <div className={`h-2 rounded-full ${
                        ml1Progress === 1 ? 'bg-emerald-500' :
                        ml1RawProgress > 0 ? 'bg-amber-400' :
                        'bg-gray-200'
                      }`} />
                      {achievedLevel === 'ML1' && (
                        <div className="absolute -top-1 left-0 right-0 h-4 ring-2 ring-offset-2 ring-blue-500 rounded-full" />
                      )}
                    </div>

                    {/* ML2 Progress */}
                    <div className="relative">
                      <div className={`h-2 rounded-full ${
                        ml2Progress === 1 ? 'bg-emerald-500' :
                        ml2RawProgress > 0 ? 'bg-amber-400' :
                        'bg-gray-200'
                      }`} />
                      {achievedLevel === 'ML2' && (
                        <div className="absolute -top-1 left-0 right-0 h-4 ring-2 ring-offset-2 ring-blue-500 rounded-full" />
                      )}
                    </div>

                    {/* ML3 Progress */}
                    <div className="relative">
                      <div className={`h-2 rounded-full ${
                        ml3Progress === 1 ? 'bg-emerald-500' :
                        ml3RawProgress > 0 ? 'bg-amber-400' :
                        'bg-gray-200'
                      }`} />
                      {achievedLevel === 'ML3' && (
                        <div className="absolute -top-1 left-0 right-0 h-4 ring-2 ring-offset-2 ring-blue-500 rounded-full" />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Updated Legend */}
        <div className="mt-6 border-t pt-4">
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div className="space-y-2">
              <h4 className="font-medium text-gray-900">Implementation Status</h4>
              <div className="space-y-2">
                <div className="flex items-center">
                  <span className="w-2 h-2 bg-emerald-500 rounded-full mr-1"></span>
                  <span>Level Achieved</span>
                </div>
                <div className="flex items-center">
                  <span className="w-2 h-2 bg-amber-400 rounded-full mr-1"></span>
                  <span>Level In Progress</span>
                </div>
                <div className="flex items-center">
                  <span className="w-2 h-2 bg-gray-200 rounded-full mr-1"></span>
                  <span>Level Not Started</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-gray-200 rounded-full ring-2 ring-offset-2 ring-blue-500 mr-1"></div>
                  <span>Current Level</span>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium text-gray-900">Maturity Level Progression</h4>
              <div className="space-y-2">
                <div className="flex items-center">
                  <span className="text-xs">ML0: Base Level (Not Implemented)</span>
                </div>
                <div className="flex items-center">
                  <span className="text-xs">ML1: Basic Controls (SME)</span>
                </div>
                <div className="flex items-center">
                  <span className="text-xs">ML2: Enterprise Controls</span>
                </div>
                <div className="flex items-center">
                  <span className="text-xs">ML3: Advanced Controls</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Implementation Guidance */}
        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
          <h4 className="text-sm font-medium text-blue-900 mb-2">Implementation & Assessment Note</h4>
          <p className="text-xs text-blue-800">
            All controls begin at ML0 and require formal assessment to achieve higher levels. Maturity levels must be implemented progressively, 
            with all controls achieving ML1 before progressing to ML2, and all at ML2 before ML3. Implementation of higher-level controls without 
            formal validation of lower levels will maintain an ML0 status.
          </p>
        </div>
      </div>

      {/* E8 Controls Status - Improved Design */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-semibold text-gray-900 mb-6">Essential Eight Controls Status</h2>
        
        <div className="space-y-8">
          {/* Each control card gets a unique subtle background color */}
          <E8ControlCard
            title="1. Patch Applications"
            description="ML1: Patches/mitigates extreme risk vulnerabilities within 48 hours"
            controlName="Patch Applications"
            maturityLevel={maturityLevel}
            answers={answers}
            calculateProgress={calculateProgress}
            className="bg-blue-50/30"
          />
          
          <E8ControlCard
            title="2. Patch Operating Systems"
            description="ML1: Patches critical vulnerabilities in 48 hours, others in 2-4 weeks"
            controlName="Patch Operating Systems"
            maturityLevel={maturityLevel}
            answers={answers}
            calculateProgress={calculateProgress}
            className="bg-purple-50/30"
          />

          <E8ControlCard
            title="3. Multi-factor Authentication"
            description="Uses multi-factor authentication for remote access and privileged actions"
            controlName="Multi-factor Authentication"
            maturityLevel={maturityLevel}
            answers={answers}
            calculateProgress={calculateProgress}
            className="bg-blue-50/30"
          />

          <E8ControlCard
            title="4. Restrict Administrative Privileges"
            description="Restricts admin privileges based on user duties"
            controlName="Restrict Administrative Privileges"
            maturityLevel={maturityLevel}
            answers={answers}
            calculateProgress={calculateProgress}
            className="bg-purple-50/30"
          />

          <E8ControlCard
            title="5. Application Control"
            description="Prevents execution of unapproved/malicious programs"
            controlName="Application Control"
            maturityLevel={maturityLevel}
            answers={answers}
            calculateProgress={calculateProgress}
            className="bg-blue-50/30"
          />

          <E8ControlCard
            title="6. Microsoft Office Macro Settings"
            description="Blocks macros from the internet and only allows vetted macros"
            controlName="Microsoft Office Macro Settings"
            maturityLevel={maturityLevel}
            answers={answers}
            calculateProgress={calculateProgress}
            className="bg-purple-50/30"
          />

          <E8ControlCard
            title="7. User Application Hardening"
            description="Configures web browsers and prevents risky functionality"
            controlName="User Application Hardening"
            maturityLevel={maturityLevel}
            answers={answers}
            calculateProgress={calculateProgress}
            className="bg-blue-50/30"
          />

          <E8ControlCard
            title="8. Regular Backups"
            description="Performs daily backups of important data and tests restoration"
            controlName="Regular Backups"
            maturityLevel={maturityLevel}
            answers={answers}
            calculateProgress={calculateProgress}
            className="bg-purple-50/30"
          />
        </div>
      </div>

      {/* Detailed Recommendations Summary */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Summary of Recommendations</h2>
        
        <div>
          <textarea
            value={summaryRecommendations}
            onChange={(e) => setSummaryRecommendations(e.target.value)}
            className="w-full h-96 p-3 border rounded-md text-sm text-gray-600"
            placeholder="Enter your recommendations for the assessment..."
          />
        </div>
      </div>

      {/* Export Actions */}
      <div className="fixed bottom-6 right-6">
        <div className="flex space-x-3">
          <button
            onClick={handleExportPDF}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Export to PDF
          </button>
          <button
            onClick={handleSaveReport}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
            </svg>
            Save Report
          </button>
        </div>
      </div>
    </div>
  );
};

const ReportsContent = ({ selectedReport, reportsMenu }) => {
  const { 
    assessmentName, 
    maturityLevel, 
    compliances, 
    answers, 
    loadAssessment, 
    savedAssessments,
    updateCompliances,
    activeTab,
    background,
    scope,
    limitations,
    setAssessmentName,
    summaryRecommendations,
    setSummaryRecommendations
  } = useAssessment();

  // Add useEffect to log state changes for debugging
  useEffect(() => {
    console.log('ReportsContent rendered with:', {
      selectedReport,
      reportsMenu,
      assessmentName,
      maturityLevel
    });
  }, [selectedReport, reportsMenu, assessmentName, maturityLevel]);

  const handleAssessmentChange = (e) => {
    const selectedAssessment = savedAssessments.find(a => a.name === e.target.value);
    if (selectedAssessment) {
      setAssessmentName(selectedAssessment.name);
      loadAssessment(selectedAssessment);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="sticky top-0 bg-white p-6 shadow-sm z-10">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-800">
            {selectedReport ? selectedReport.name : 'Select a Report'}
          </h1>
          
          {/* Assessment Selector Dropdown */}
          <div className="relative">
            <select
              className="block w-64 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              onChange={handleAssessmentChange}
              value={assessmentName || ''}
            >
              <option value="">Select an Assessment</option>
              {savedAssessments?.map((assessment) => (
                <option key={assessment.name} value={assessment.name}>
                  {assessment.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <div className="p-6">
        {!selectedReport ? (
          <div className="text-center text-gray-500 mt-10">
            <div className="max-w-md mx-auto">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No report selected</h3>
              <p className="mt-1 text-sm text-gray-500">
                Select a report type from the sidebar to view its contents
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {selectedReport.id === 1 && (
              <ExecutiveSummaryReport 
                assessmentName={assessmentName}
                maturityLevel={maturityLevel}
                compliances={compliances}
                answers={answers || {}}
                summaryRecommendations={summaryRecommendations}
                setSummaryRecommendations={setSummaryRecommendations}
              />
            )}
            {selectedReport.id === 2 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Control Assessment</h2>
                <p className="text-gray-600">Control assessment content coming soon...</p>
              </div>
            )}
            {selectedReport.id === 3 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Gap Analysis</h2>
                <p className="text-gray-600">Gap analysis content coming soon...</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsContent;
